/**
 * ProposalListRow - Linha de proposta reutilizável
 *
 * Fonte única da linha da listagem de propostas, usada por:
 *  - aba "Propostas" do modal de NEGÓCIO (DealProposalsTab)
 *  - aba "Propostas" dos modais de CLIENTE/CONTATO (CustomerProposalsTab)
 *
 * Mantém em um só lugar as regras visuais e os atalhos de ação (compartilhar,
 * link público, PDF e transições de situação), evitando divergência entre as
 * duas telas. As regras de permissão continuam no servidor; aqui apenas
 * refletimos o que o usuário pode acionar.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-03 v1.1.0 - Trava do processo (workflow) aplicada em "aceitar" e "enviar"
 *   RELEASE: 2026-08-12 v1.0.0 - Extração de DealProposalsTab para reuso
 */


import React from 'react';
import {
  AlertTriangle,
  Ban,
  Briefcase,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  Loader2,
  RefreshCw,
  Send,
  Share2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  buildPublicProposalUrl,
  PROPOSAL_STATUS_COLORS,
  PROPOSAL_STATUS_LABELS,
  type DealProposal,
  type ProposalStatus,
} from '@/hooks/useDealProposals';
// v1.1.0: trava do processo explicada antes do clique
import { useWorkflowGuardCheck } from '@/hooks/useWorkflowGuardCheck';
import { WorkflowGuardGate } from '@/components/workflow/WorkflowGuardGate';

/** Situações em que a proposta ainda aceita transições/compartilhamento */
const ACTIONABLE_STATUSES: ProposalStatus[] = ['draft', 'generated', 'sent', 'viewed'];

/** Situações sem validade comercial — não podem ser exportadas em PDF */
const EXPORT_BLOCKED_STATUSES: ProposalStatus[] = [
  'superseded',
  'cancelled',
  'expired',
  'rejected',
];

/** Formata valores monetários */
function formatCurrency(value: number | null | undefined, currency = 'BRL'): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** Formata data curta */
function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
}

export interface ProposalListRowProps {
  /** Proposta exibida na linha */
  proposal: DealProposal;
  /** Permite acionar transições de situação e compartilhamento */
  canAct: boolean;
  /** Negócio de origem (exibido apenas na listagem por cliente/contato) */
  dealLabel?: string | null;
  /** Abre o modal de detalhes */
  onOpenDetails: (proposalId: string) => void;
  /** Abre o modal de compartilhamento */
  onShare: (proposalId: string) => void;
  /** Copia o link público já existente */
  onCopyLink: (proposal: DealProposal) => void;
  /** Gera o link público (quando ainda não existe) */
  onGenerateLink: (proposal: DealProposal) => void;
  /** Indica geração de link em andamento */
  isGeneratingLink?: boolean;
  /** Transição de situação */
  onStatus: (proposal: DealProposal, status: 'sent' | 'accepted' | 'rejected' | 'cancelled') => void;
  /** Abre o preview interno do PDF */
  onPreviewPdf: (proposal: DealProposal, storagePath: string) => void;
  /** Baixa o PDF já pronto */
  onDownloadPdf: (storagePath: string | null) => void;
  /** Solicita a geração do PDF */
  onGeneratePdf: (proposal: DealProposal) => void;
  /** ID da proposta cujo PDF está sendo gerado */
  pdfBusyId?: string | null;
  /** Geração de PDF em andamento (global) */
  isPdfPending?: boolean;
  /**
   * v1.1.0: negócio de origem da proposta. Quando informado, a linha consulta a
   * trava do processo (workflow) para "aceitar"/"enviar".
   */
  dealId?: string | null;
}

export function ProposalListRow({
  proposal: p,
  canAct: canActProp,
  dealLabel,
  onOpenDetails,
  onShare,
  onCopyLink,
  onGenerateLink,
  isGeneratingLink = false,
  onStatus,
  onPreviewPdf,
  onDownloadPdf,
  onGeneratePdf,
  pdfBusyId = null,
  isPdfPending = false,
  dealId = null,
}: ProposalListRowProps) {
  const status = p.status as ProposalStatus;
  const canAct = canActProp && ACTIONABLE_STATUSES.includes(status);
  const isExportBlocked = EXPORT_BLOCKED_STATUSES.includes(status);
  // Gerar link só nas situações compartilháveis (substituída/cancelada têm link revogado)
  const canGenerateLink = ACTIONABLE_STATUSES.includes(status);

  /*
   * v1.1.0: a aba Produtos é a fonte de verdade do negócio. Se o valor mudou e o
   * processo passou a exigir aprovação, as propostas JÁ criadas também não podem
   * ser aceitas/enviadas. Consulta antecipada da política (a decisão real segue
   * no banco); falha ou carregamento nunca bloqueiam a interface.
   */
  const dealIdOrigem = dealId ?? ((p as { deal_id?: string | null }).deal_id ?? null);
  const guardAceitar = useWorkflowGuardCheck({
    actionCode: 'crm.proposal.accept',
    rootEntityType: 'crm_deal',
    rootEntityId: dealIdOrigem,
    enabled: canAct && !!dealIdOrigem,
  });
  const guardEnviar = useWorkflowGuardCheck({
    actionCode: 'crm.proposal.send',
    rootEntityType: 'crm_deal',
    rootEntityId: dealIdOrigem,
    enabled: canAct && !!dealIdOrigem,
  });




  return (
    <div
      className="flex items-center gap-3 p-2.5 hover:bg-muted/40 transition-colors cursor-pointer"
      onClick={() => onOpenDetails(p.id)}
    >
      {/* Situação (largura fixa p/ alinhamento vertical entre linhas) */}
      <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs whitespace-nowrap">
        <span className={cn('h-2 w-2 rounded-full', PROPOSAL_STATUS_COLORS[status])} />
        {PROPOSAL_STATUS_LABELS[status]}
      </span>

      {/* Valor */}
      <span className="w-28 shrink-0 text-right text-sm font-medium whitespace-nowrap">
        {formatCurrency(p.total, p.currency)}
      </span>

      {/* Descrição da proposta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{p.title || p.proposal_number}</span>
          <span className="text-[10px] text-muted-foreground">v{p.version_number}</span>
          {p.is_out_of_sync && ['generated', 'sent', 'viewed'].includes(status) && (
            <span
              className="flex items-center gap-1 text-[10px] text-amber-600"
              title="Os produtos do negócio mudaram após esta proposta"
            >
              <AlertTriangle className="h-3 w-3" />
              desatualizada
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {p.proposal_number} · {p.items_count} itens · válida até {formatDate(p.expires_at)}
        </p>
        {/* Negócio de origem (listagem por cliente/contato) */}
        {dealLabel && (
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground/80 truncate">
            <Briefcase className="h-3 w-3 shrink-0" />
            {dealLabel}
          </p>
        )}
      </div>

      {/* PDF: visualizar + baixar quando pronto, gerar/regerar caso contrário */}
      <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
        {isExportBlocked ? null : p.pdf_status === 'ready' && p.pdf_storage_path ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              title="Visualizar PDF da proposta"
              onClick={() => onPreviewPdf(p, p.pdf_storage_path!)}
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              title="Baixar PDF da proposta"
              onClick={() => onDownloadPdf(p.pdf_storage_path)}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            title={p.pdf_status === 'error' ? 'Falha ao gerar — tentar novamente' : 'Gerar PDF'}
            disabled={pdfBusyId === p.id || isPdfPending}
            onClick={() => onGeneratePdf(p)}
          >
            {pdfBusyId === p.id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : p.pdf_status === 'error' ? (
              <RefreshCw className="h-3.5 w-3.5 text-destructive" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      {/* Atalhos em ícone (evita abrir o menu Ações / modal de detalhes) */}
      {canAct && (
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title="Compartilhar proposta"
            aria-label="Compartilhar proposta"
            onClick={() => onShare(p.id)}
          >
            <Share2 className="h-3.5 w-3.5" />
          </Button>

          {/* Link público: copiar/abrir quando existe; gerar quando não existe */}
          {p.public_enabled && p.public_token ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Copiar link público"
                aria-label="Copiar link público"
                onClick={() => onCopyLink(p)}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Abrir link público"
                aria-label="Abrir link público"
                onClick={() =>
                  window.open(buildPublicProposalUrl(p.public_token!), '_blank', 'noopener')
                }
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            /* Gerar link só enquanto a proposta é compartilhável (revogação) */
            canGenerateLink && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Gerar link público"
                aria-label="Gerar link público"
                disabled={isGeneratingLink}
                onClick={() => onGenerateLink(p)}
              >
                <Link2 className="h-3.5 w-3.5" />
              </Button>
            )
          )}

          {/* Transições de situação */}
          {/* v1.1.0: aceitar respeita a trava do processo do negócio */}
          <WorkflowGuardGate decisao={guardAceitar}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              title="Marcar como aceita"
              aria-label="Marcar como aceita"
              disabled={guardAceitar.bloqueado}
              onClick={() => onStatus(p, 'accepted')}
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </Button>
          </WorkflowGuardGate>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title="Marcar como recusada"
            aria-label="Marcar como recusada"
            onClick={() => onStatus(p, 'rejected')}
          >
            <XCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title="Cancelar proposta"
            aria-label="Cancelar proposta"
            onClick={() => onStatus(p, 'cancelled')}
          >
            <Ban className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      )}

      {/* Menu "Ações" (mantido para paridade com o modal de detalhes) */}
      {canAct && (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                Ações
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {p.public_enabled && p.public_token ? (
                <>
                  <DropdownMenuItem onClick={() => onCopyLink(p)}>
                    <Copy className="h-3.5 w-3.5 mr-2" />
                    Copiar link público
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(buildPublicProposalUrl(p.public_token!), '_blank', 'noopener')
                    }
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-2" />
                    Abrir link público
                  </DropdownMenuItem>
                </>
              ) : (
                canGenerateLink && (
                  <DropdownMenuItem disabled={isGeneratingLink} onClick={() => onGenerateLink(p)}>
                    <Link2 className="h-3.5 w-3.5 mr-2" />
                    Gerar link público
                  </DropdownMenuItem>
                )
              )}
              <DropdownMenuSeparator />
              {['draft', 'generated'].includes(status) && (
                <DropdownMenuItem
                  className="gap-2"
                  disabled={guardEnviar.bloqueado}
                  onClick={() => onStatus(p, 'sent')}
                >
                  <Send className="h-4 w-4 text-muted-foreground" />
                  Marcar como enviada
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="gap-2"
                disabled={guardAceitar.bloqueado}
                onClick={() => onStatus(p, 'accepted')}
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                Marcar como aceita
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => onStatus(p, 'rejected')}>
                <XCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Marcar como recusada
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-destructive focus:text-destructive"
                onClick={() => onStatus(p, 'cancelled')}
              >
                <Ban className="h-4 w-4" />
                Cancelar proposta
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
